<script lang="ts" setup>
  import moment from 'moment';
  import { ref, computed, watch } from 'vue';
  import { VDatePicker } from 'vuetify/components';

  import { BvBtn } from '@bv/components';
  import type {
    AvailableDateRangeKey,
    CustomDateRange,
    CustomDateRangeWithId,
  } from '@bv/components/form/inputs/date/partials/DateInput.type';
  import { trans } from '@bv/utils';
  import { toSystemColor } from '@bv/utils/common/color/to-system-color';

  type InternalCustomDateRange = CustomDateRangeWithId & { disabled?: boolean; isActive?: boolean };

  type AvailableDateRange = InternalCustomDateRange & { id: AvailableDateRangeKey };

  // Use defineModel for v-model binding
  // For single: string, For range: [startDate, endDate] array
  const modelValue = defineModel<string | [string, string] | null>({ default: null });

  const props = withDefaults(
    defineProps<{
      type?: 'single' | 'range';
      min?: string;
      max?: string;
      disabled?: boolean;
      hideHeader?: boolean;
      showAdjacentMonths?: boolean;
      withRanges?: boolean | AvailableDateRangeKey[];
      bgColor?: string;
      color?: string;
      customRanges?: CustomDateRange[];
    }>(),
    {
      type: 'single',
      min: undefined,
      max: undefined,
      disabled: false,
      hideHeader: true,
      showAdjacentMonths: true,
      withRanges: false,
      bgColor: undefined,
      color: 'base',
      customRanges: () => [],
    },
  );

  const colors = computed(() => ({
    color: toSystemColor(props.color),
    bgColor: toSystemColor(props.bgColor || 'transparent'),
  }));

  const dates = ref<string[] | string>([]);

  // Computed property to determine the multiple prop for v-date-picker
  const datePickerMultiple = computed(() => {
    return props.type === 'range' ? 'range' : false;
  });

  const customRanges = computed<CustomDateRangeWithId[]>(() => {
    return props.customRanges.map((range: CustomDateRange, i) => ({ ...range, id: i }));
  });

  const startSelectedDate = computed<string | null>(() => {
    const date = Array.isArray(dates.value) ? dates.value[0] : dates.value || null;
    return date ? moment(date).format('YYYY-MM-DD') : null;
  });

  const endSelectedDate = computed<string | null>(() => {
    const date = Array.isArray(dates.value) ? dates.value[dates.value.length - 1] : dates.value || null;
    return date ? moment(date).format('YYYY-MM-DD') : null;
  });

  const defaultRanges = computed<AvailableDateRange[]>(() => {
    if (!props.withRanges) {
      return [];
    }

    const ranges: AvailableDateRange[] = [
      {
        id: 'today',
        title: trans('global.form.date.ranges.today_trans'),
        start: moment().format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
      },
      {
        id: 'yesterday',
        title: trans('global.form.date.ranges.yesterday_trans'),
        start: moment().subtract(1, 'day').format('YYYY-MM-DD'),
        end: moment().subtract(1, 'day').format('YYYY-MM-DD'),
      },
      {
        id: 'last_7_days',
        title: trans('global.form.date.ranges.last_7_days_trans'),
        start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
      },
      {
        id: 'current_month',
        title: trans('global.form.date.ranges.current_month_trans'),
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
      },
      {
        id: 'last_30_days',
        title: trans('global.form.date.ranges.last_30_days_trans'),
        start: moment().subtract(29, 'days').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
      },
      {
        id: 'last_month',
        title: trans('global.form.date.ranges.last_month_trans'),
        start: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        end: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      },
    ];
    return props.withRanges === true
      ? ranges
      : ranges.filter((range) => (props.withRanges as AvailableDateRangeKey[]).includes(range.id));
  });

  const allCustomRanges = computed<InternalCustomDateRange[]>(() => {
    const ranges: InternalCustomDateRange[] = [...defaultRanges.value, ...customRanges.value];

    const mappedRanges: InternalCustomDateRange[] = ranges.map((range) => {
      if (
        (props.min && moment(range.start).isBefore(moment(props.min).startOf('day'))) ||
        (props.max && moment(range.end).isAfter(moment(props.max).endOf('day'))) ||
        !moment(range.start).isValid() ||
        !moment(range.end).isValid()
      ) {
        range.disabled = true;
      }

      if (
        startSelectedDate.value &&
        endSelectedDate.value &&
        moment(startSelectedDate.value).isSame(moment(range.start), 'day') &&
        moment(endSelectedDate.value).isSame(moment(range.end), 'day')
      ) {
        range.isActive = true;
      } else {
        range.isActive = false;
      }

      return range;
    });

    return mappedRanges;
  });

  /**
   * Convert a DateValue (string or [start, end] array) to dates array for calendar display
   */
  const convertToDates = (value: string | [string, string] | null): string[] | string => {
    if (!value) {
      return [];
    }

    if (props.type === 'single') {
      // Single mode: string format
      if (typeof value === 'string') {
        return moment(value).format('YYYY-MM-DD');
      }
      // If array passed to single mode, use first element
      return moment(value[0]).format('YYYY-MM-DD');
    }

    // For range mode: [startDate, endDate]
    if (typeof value === 'string') {
      // String passed to range mode - treat as single date range
      return [moment(value).format('YYYY-MM-DD')];
    }

    // Array format [startDate, endDate]
    const startDate = moment(value[0]);
    const endDate = moment(value[1]).endOf('day');
    const allDates: string[] = [];
    const currentDate = moment(startDate);

    while (currentDate.isSameOrBefore(endDate)) {
      allDates.push(currentDate.format('YYYY-MM-DD'));
      currentDate.add(1, 'day');
    }

    return allDates;
  };

  /**
   * Check if a date is within the allowed min/max range
   */
  const isDateInRange = (date: Date): boolean => {
    const momentDate = moment(date).startOf('day');
    if (props.min && momentDate.isBefore(moment(props.min).startOf('day'))) {
      return false;
    }
    if (props.max && momentDate.isAfter(moment(props.max).startOf('day'))) {
      return false;
    }
    return true;
  };

  /**
   * Generate all dates between start and end date for calendar display
   */
  const generateDateRange = (startDate: moment.Moment, endDate: moment.Moment): string[] => {
    const allDates: string[] = [];
    const currentDate = moment(startDate);

    while (currentDate.isSameOrBefore(endDate)) {
      if (isDateInRange(currentDate.toDate())) {
        allDates.push(currentDate.format('YYYY-MM-DD'));
      }
      currentDate.add(1, 'day');
    }

    return allDates;
  };

  /**
   * Set a selected date range
   * Output format: string for single, [startDate, endDate] for range
   */
  const setRange = (rangeKey: (typeof allCustomRanges.value)[number]['id']): void => {
    if (props.disabled) {
      return;
    }

    const customRange = allCustomRanges.value?.find((range) => range.id === rangeKey);
    if (customRange && !customRange.disabled) {
      const startDate = moment(customRange.start);
      const endDate = moment(customRange.end);

      dates.value = generateDateRange(startDate, endDate);
      handleDateSelection(dates.value);
    }
  };

  /**
   * Handle date selection from calendar
   * Vuetify returns array of all selected dates in range mode
   * We need to extract only startDate (first) and endDate (last)
   * @param newValue - The value from Vuetify's update:model-value event
   * Output: string for single, [startDate, endDate] for range
   */
  const handleDateSelection = (newValue: unknown): void => {
    if (props.disabled) {
      return;
    }

    // For single date mode - return string
    if (props.type === 'single') {
      // Handle various input types from Vuetify
      if (newValue && (typeof newValue === 'string' || newValue instanceof Date)) {
        modelValue.value = moment(newValue).format('YYYY-MM-DD');
      } else if (Array.isArray(newValue) && newValue.length > 0) {
        modelValue.value = moment(newValue[0]).format('YYYY-MM-DD');
      } else if (newValue && typeof newValue === 'object') {
        modelValue.value = moment(newValue as Date).format('YYYY-MM-DD');
      } else {
        modelValue.value = null;
      }
      return;
    }

    // For range mode - return [startDate, endDate]
    const datesArray = Array.isArray(newValue) ? newValue : newValue ? [newValue] : [];

    if (datesArray && datesArray.length > 0) {
      const sortedDates = [...datesArray].sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
      const startDate = moment(sortedDates[0]).format('YYYY-MM-DD');
      const endDate = moment(sortedDates[sortedDates.length - 1]).format('YYYY-MM-DD');

      modelValue.value = [startDate, endDate];
    } else {
      modelValue.value = null;
    }
  };

  /**
   * Watch for external modelValue changes
   */
  watch(
    () => modelValue.value,
    (newValue) => {
      if (newValue) {
        dates.value = convertToDates(newValue);
      } else {
        dates.value = [];
      }
    },
    { deep: true, immediate: true },
  );
</script>

<template>
  <div class="bv-calendar flex">
    <!-- Predefined Ranges Sidebar (only show if ranges exist AND in range mode) -->
    <div
      v-if="allCustomRanges.length > 0 && type === 'range'"
      class="max-h-83 max-w-36 space-y-1 overflow-y-auto p-2"
    >
      <bv-btn
        v-for="range in allCustomRanges"
        :key="range.id"
        class="h-auto w-full justify-start p-2"
        :color="!range.disabled && range.isActive ? colors.color : 'gray'"
        :disabled="range.disabled"
        :text="range.title"
        variant="text"
        @click="!range.disabled && setRange(range.id)"
      />
    </div>

    <!-- Calendar -->
    <v-date-picker
      v-model="dates"
      :bg-color="colors.bgColor"
      :color="colors.color"
      :disabled="disabled"
      :hide-header="hideHeader"
      :max="max"
      :min="min"
      :multiple="datePickerMultiple"
      :show-adjacent-months="showAdjacentMonths"
      @update:model-value="handleDateSelection"
    />
  </div>
</template>

<style lang="scss" scoped>
  /* change the color of the selected date Number */
  :deep(.v-date-picker-month__day.v-date-picker-month__day--selected .v-btn .v-btn__content) {
    color: var(--color-gray-900) !important;
    font-size: 12px;
  }

  /* Default: change the color of all selected dates */
  :deep(.v-date-picker-month__day.v-date-picker-month__day--selected .v-date-picker-month__day-btn) {
    background-color: var(--color-base-100) !important;
  }

  :deep(.v-btn--variant-outlined.v-date-picker-month__day-btn) {
    color: v-bind('colors.color') !important;
  }

  /* change the color of the selected month and year */
  :deep(.v-date-picker-months__content .v-btn.v-btn--active .v-btn__content) {
    color: var(--color-white);
  }
  :deep(.v-date-picker-years__content .v-btn.v-btn--active .v-btn__content) {
    color: var(--color-white);
  }

  /* change days in last month that selected  */
  :deep(.v-date-picker-month__day.v-date-picker-month__day--adjacent.v-date-picker-month__day--selected) {
    opacity: 0.8;
  }
</style>
